import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, MoreVertical, Phone, MessageCircle, CreditCard, MapPin, Calendar, Users, Eye, Ticket, Copy, Pencil, Loader2 } from 'lucide-react';
import { cachedGet, invalidate } from '../lib/cache';
import { inrFull, dateRange, pax, stageMeta, tripMoney, waNumber } from '../lib/ui';
import FollowUpManager from '@/components/followup/FollowUpManager';
import SendWhatsAppDialog from '@/components/SendWhatsAppDialog';
import { buildTripWaContext, tripSuggestHint, resolveTripPaymentLink } from '@/utils/waContext';
import { useAuth } from '@/contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STAGES = [
  { key: 'enquiry', label: 'Enquiry', status: 'draft' },
  { key: 'quote', label: 'Quote', status: 'sent' },
  { key: 'booked', label: 'Booked', status: 'confirmed' },
  { key: 'ongoing', label: 'Ongoing', status: 'confirmed' },
  { key: 'completed', label: 'Completed', status: 'completed' },
];
const dOf = (s) => { if (!s) return null; const d = new Date(s); return isNaN(d.getTime()) ? null : d; };

const VISA_CHIPS = {
  not_required: { label: 'Visa: Not required', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
  applied: { label: 'Visa: Applied', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  approved: { label: 'Visa: Approved', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  rejected: { label: 'Visa: Rejected', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
  expired: { label: 'Visa: Expired', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
};
const activeStage = (t) => {
  const st = (t.status || 'draft').toLowerCase();
  if (st === 'completed') return 'completed';
  if (['confirmed', 'accepted'].includes(st)) {
    const a = dOf(t.travelStartDate); const b = dOf(t.travelEndDate) || a;
    const now = new Date(); const today = new Date(); today.setHours(0, 0, 0, 0);
    return (a && b && a <= now && b >= today) ? 'ongoing' : 'booked';
  }
  if (st === 'sent') return 'quote';
  return 'enquiry';
};

export const MobileTripDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [trip, setTrip] = useState(null);
  const [itin, setItin] = useState(null);
  const [busy, setBusy] = useState(false);
  const [menu, setMenu] = useState(false);
  const [waOpen, setWaOpen] = useState(false);
  const [waPaymentLink, setWaPaymentLink] = useState('');

  useEffect(() => {
    let alive = true;
    cachedGet(`/api/quotes/${id}`).then((q) => {
      if (!alive) return;
      setTrip(q);
      if (q.itineraryId) cachedGet(`/api/itineraries/${q.itineraryId}`).then((it) => alive && setItin(it)).catch(() => {});
    }).catch(() => toast.error('Could not load trip'));
    return () => { alive = false; };
  }, [id]);

  const money = useMemo(() => (trip ? tripMoney(trip) : { total: 0, received: 0, outstanding: 0 }), [trip]);

  if (!trip) {
    return (
      <div className="fixed inset-0 bg-white flex items-center justify-center" data-testid="m-trip-detail-loading">
        <Loader2 className="h-6 w-6 animate-spin text-[#0F6E56]" />
      </div>
    );
  }

  const stage = activeStage(trip);
  const meta = stageMeta(trip);
  const name = trip.guestName && trip.guestName !== '(Pending guest)' ? trip.guestName : (trip.destination || 'Trip');

  const setStage = async (s) => {
    if (busy || s.key === stage) return;
    setBusy(true);
    try {
      if (s.status === 'completed') {
        await axios.post(`${BACKEND_URL}/api/trips/${trip.id}/complete`, {}, { withCredentials: true });
        toast.success('Trip completed');
        setTrip((t) => ({ ...t, status: 'completed' }));
      } else {
        await axios.patch(`${BACKEND_URL}/api/quotes/${trip.id}/status`, { status: s.status }, { withCredentials: true });
        toast.success(`Marked as ${s.label}`);
        setTrip((t) => ({ ...t, status: s.status }));
      }
      invalidate('/api/trips'); invalidate(`/api/quotes/${trip.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Status update failed');
    } finally { setBusy(false); }
  };

  const itinRef = itin?.public_ref || itin?.id;
  let base = window.location.origin;
  try { if (itin?.public_url) base = new URL(itin.public_url).origin; } catch (_) { /* noop */ }
  const previewUrl = itinRef ? `${base}/${itinRef}/preview` : '';
  const voucherUrl = itinRef ? `${base}/${itinRef}/voucher` : '';
  const copy = async (url, label) => {
    if (!url) { toast.error('Create an itinerary first'); return; }
    setMenu(false);
    try { await navigator.clipboard.writeText(url); toast.success(`${label} link copied`); } catch { window.prompt(`Copy ${label}:`, url); }
  };

  const wa = waNumber(trip.countryCode || '+91', trip.phone);

  const openWa = async () => {
    if (!trip.phone) { toast.error('No phone on file for this trip'); return; }
    const pl = await resolveTripPaymentLink(trip.tripId);
    setWaPaymentLink(pl);
    setWaOpen(true);
  };

  // BUG 5 — per-pax visa status for THIS trip's destination.
  const extraPax = trip.additionalPax || [];
  const paxRows = [
    { name, visa: trip.primaryVisaStatus, primary: true },
    ...extraPax.map((p) => ({ name: p.name, visa: p.visaStatus })),
  ].filter((p) => p.name);

  return (
    <div className="fixed inset-0 bg-[#F8F9FA] flex flex-col" data-testid="m-trip-detail">
      {/* Top bar */}
      <header className="h-[52px] bg-white border-b border-[#E5E7EB] flex items-center gap-2 px-2 shrink-0">
        <button type="button" onClick={() => navigate(-1)} data-testid="m-trip-back" aria-label="Back" className="w-11 h-11 flex items-center justify-center text-[#111827]"><ArrowLeft className="h-5 w-5" /></button>
        <div className="min-w-0 flex-1">
          <p className="text-[15px] font-semibold text-[#111827] truncate leading-tight">{name}</p>
          <p className="text-[11px] text-[#6B7280] truncate">{trip.displayTripId || trip.tripId}</p>
        </div>
        <span className={`shrink-0 px-2.5 py-1 rounded-full text-[11px] font-semibold ${meta.cls}`} data-testid="m-trip-status-pill">{meta.label}</span>
        <div className="relative">
          <button type="button" onClick={() => setMenu((m) => !m)} data-testid="m-trip-menu" aria-label="More" className="w-11 h-11 flex items-center justify-center text-[#111827]"><MoreVertical className="h-5 w-5" /></button>
          {menu && (
            <>
              <button type="button" aria-label="Close" onClick={() => setMenu(false)} className="fixed inset-0 z-40" />
              <div className="absolute right-1 top-12 z-50 w-52 bg-white border border-[#E5E7EB] rounded-xl overflow-hidden" data-testid="m-trip-share-menu">
                <button type="button" onClick={() => copy(previewUrl, 'Preview')} data-testid="m-share-preview" className="w-full flex items-center gap-2.5 px-3.5 min-h-[48px] text-[14px] text-[#111827] active:bg-[#F8F9FA]"><Eye className="h-4 w-4 text-[#6B7280]" /> Share Preview <Copy className="h-3.5 w-3.5 ml-auto text-[#6B7280]" /></button>
                <button type="button" onClick={() => copy(voucherUrl, 'Voucher')} data-testid="m-share-voucher" className="w-full flex items-center gap-2.5 px-3.5 min-h-[48px] text-[14px] text-[#111827] border-t border-[#E5E7EB] active:bg-[#F8F9FA]"><Ticket className="h-4 w-4 text-[#6B7280]" /> Share Voucher <Copy className="h-3.5 w-3.5 ml-auto text-[#6B7280]" /></button>
                <button type="button" onClick={() => { setMenu(false); navigate(`/quotes/${trip.id}/edit`); }} data-testid="m-trip-edit" className="w-full flex items-center gap-2.5 px-3.5 min-h-[48px] text-[14px] text-[#111827] border-t border-[#E5E7EB] active:bg-[#F8F9FA]"><Pencil className="h-4 w-4 text-[#6B7280]" /> Edit trip</button>
              </div>
            </>
          )}
        </div>
      </header>

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4" style={{ paddingBottom: '88px' }}>
        {/* Payment summary card */}
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-4" data-testid="m-trip-payment-card">
          <p className="text-[12px] font-semibold text-[#6B7280] uppercase tracking-wide mb-3">Payment</p>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div><p className="text-[11px] text-[#6B7280]">Total</p><p className="text-[15px] font-bold text-[#111827] mt-0.5">{inrFull(money.total)}</p></div>
            <div><p className="text-[11px] text-[#6B7280]">Received</p><p className="text-[15px] font-bold text-emerald-600 mt-0.5">{inrFull(money.received)}</p></div>
            <div><p className="text-[11px] text-[#6B7280]">Outstanding</p><p className={`text-[15px] font-bold mt-0.5 ${money.outstanding > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>{inrFull(money.outstanding)}</p></div>
          </div>
        </div>

        {/* Status pills */}
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-3.5" data-testid="m-trip-stage-pills">
          <p className="text-[12px] font-semibold text-[#6B7280] uppercase tracking-wide mb-2.5">Update status</p>
          <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1" style={{ scrollbarWidth: 'none' }}>
            {STAGES.map((s) => {
              const on = s.key === stage;
              return (
                <button key={s.key} type="button" onClick={() => setStage(s)} disabled={busy} data-testid={`m-stage-${s.key}`}
                  className="shrink-0 px-3 min-h-[40px] rounded-[20px] text-[13px] font-semibold border"
                  style={on ? { backgroundColor: '#0F6E56', color: '#fff', borderColor: '#0F6E56' } : { backgroundColor: '#fff', color: '#6B7280', borderColor: '#E5E7EB' }}>
                  {s.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Trip meta */}
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-4 space-y-2.5" data-testid="m-trip-meta">
          {trip.destination && <Meta icon={MapPin} text={trip.destination} />}
          <Meta icon={Calendar} text={dateRange(trip.travelStartDate, trip.travelEndDate) || 'Dates not set'} />
          <Meta icon={Users} text={pax(trip) ? `${pax(trip)} traveller${pax(trip) === 1 ? '' : 's'}` : 'Pax not set'} />
          {trip.phone && <Meta icon={Phone} text={trip.phone} />}
        </div>

        {/* Travellers & visa status (per trip) */}
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-4" data-testid="m-trip-travellers">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[12px] font-semibold text-[#6B7280] uppercase tracking-wide">Travellers &amp; visa</p>
            <button type="button" onClick={() => navigate(`/quotes/${trip.id}/edit`)} data-testid="m-trip-edit-travellers" className="text-[12px] font-semibold text-[#0F6E56]">Edit</button>
          </div>
          <div className="space-y-2">
            {paxRows.map((p, i) => {
              const v = VISA_CHIPS[p.visa];
              return (
                <div key={i} className="flex items-center justify-between gap-2" data-testid={`m-trip-pax-${i}`}>
                  <span className="text-[14px] text-[#111827] truncate flex items-center gap-1.5">
                    {p.name}{p.primary && <span className="text-[10px] font-semibold text-[#0F6E56] bg-[#E7F1ED] px-1.5 py-0.5 rounded-full">Primary</span>}
                  </span>
                  <span className={`shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${v ? v.cls : 'bg-slate-50 text-slate-400 border-slate-200'}`}>
                    {v ? v.label : 'Visa: —'}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Follow-ups (manual + auto-suggested) */}
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-4" data-testid="m-trip-followups">
          <FollowUpManager tripId={trip.tripId} />
        </div>
      </div>

      {/* Sticky bottom actions */}
      <div className="bg-white border-t border-[#E5E7EB] px-3 py-2.5 flex gap-2 shrink-0" style={{ paddingBottom: 'calc(10px + env(safe-area-inset-bottom))' }} data-testid="m-trip-actions">
        <a href={trip.phone ? `tel:${trip.phone}` : undefined} data-testid="m-trip-call" className={`flex-1 h-11 rounded-lg border border-[#E5E7EB] flex items-center justify-center gap-1.5 text-[13px] font-medium ${trip.phone ? 'text-[#111827]' : 'text-[#9CA3AF] pointer-events-none'}`}><Phone className="h-4 w-4" /> Call</a>
        <button type="button" onClick={openWa} data-testid="m-trip-wa" className={`flex-1 h-11 rounded-lg border border-[#E5E7EB] flex items-center justify-center gap-1.5 text-[13px] font-medium ${trip.phone ? 'text-[#0F6E56]' : 'text-[#9CA3AF]'}`}><MessageCircle className="h-4 w-4" /> WhatsApp</button>
        <button type="button" onClick={() => navigate(`/payment-links?tripId=${trip.tripId}`)} data-testid="m-trip-paylink" className="flex-1 h-11 rounded-lg flex items-center justify-center gap-1.5 text-[13px] font-medium text-white" style={{ backgroundColor: '#0F6E56' }}><CreditCard className="h-4 w-4" /> Pay link</button>
      </div>

      <SendWhatsAppDialog
        open={waOpen}
        onOpenChange={setWaOpen}
        title={name}
        phone={wa}
        context={buildTripWaContext(trip, { itinerary: itin, user, outstanding: money.outstanding, paymentLink: waPaymentLink })}
        suggestHint={tripSuggestHint(trip, money.outstanding)}
      />
    </div>
  );
};

const Meta = ({ icon: Icon, text }) => (
  <div className="flex items-center gap-2.5 text-[14px] text-[#111827]"><Icon className="h-4 w-4 text-[#6B7280] shrink-0" /><span className="truncate">{text}</span></div>
);

export default MobileTripDetail;
